/**
 * Photo Preview Modal Component
 * Full-screen hi-res photo viewer with download option
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { PhotoData, PhotoCategory } from '../../../../shared/types/project';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../../shared/types/project';
import { api } from '../../../services/api';
import { CategoryIconDisplay } from './CategoryManagerModal';
import { LocalImage, preloadImage } from '../../../components/common/LocalImage';
import '../../../components/modals/ProjectModal.css';

interface PhotoPreviewModalProps {
  isOpen: boolean;
  photos: PhotoData[];  // Can be single photo or group of photos at same location
  /** All project photos for chronological navigation (optional) */
  allPhotos?: PhotoData[];
  initialPhotoId?: string;
  customCategories: PhotoCategory[];
  onClose: () => void;
  onEditPhoto?: (photo: PhotoData) => void;
  onDeletePhoto?: (photoId: string) => void;
  /** Called when navigating chronologically to a photo outside this group */
  onNavigateToPhoto?: (photo: PhotoData) => void;
}

export function PhotoPreviewModal({
  isOpen,
  photos,
  allPhotos,
  initialPhotoId,
  customCategories,
  onClose,
  onEditPhoto,
  onDeletePhoto,
  onNavigateToPhoto,
}: PhotoPreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showInfo, setShowInfo] = useState(true);

  // Zoom and pan - use refs for smooth direct DOM manipulation
  const [zoomDisplay, setZoomDisplay] = useState(1); // Only for UI display
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const allCategories = [...DEFAULT_PHOTO_CATEGORIES, ...customCategories];

  // ── Chronological navigation ──────────────────────────────────────
  const chronologicalPhotos = useMemo(() => {
    if (!allPhotos || allPhotos.length === 0) return [];
    return [...allPhotos].sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });
  }, [allPhotos]);

  const currentPhoto = photos[currentIndex];

  const chronoIndex = useMemo(() => {
    if (!currentPhoto || chronologicalPhotos.length === 0) return -1;
    return chronologicalPhotos.findIndex(p => p.id === currentPhoto.id);
  }, [currentPhoto, chronologicalPhotos]);

  // Apply transform directly to DOM for smooth updates
  const applyTransform = useCallback(() => {
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.transform =
        `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoomRef.current})`;
    }
  }, []);

  // Set initial photo when modal opens
  useEffect(() => {
    if (isOpen && initialPhotoId) {
      const idx = photos.findIndex(p => p.id === initialPhotoId);
      if (idx !== -1) {
        setCurrentIndex(idx);
      }
    }
    setImageLoaded(false);
    // Reset zoom/pan when modal opens
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoomDisplay(1);
    applyTransform();
  }, [isOpen, initialPhotoId, photos, applyTransform]);

  // Reset zoom/pan when changing photos
  const resetZoom = useCallback(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoomDisplay(1);
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.transition = 'transform 0.2s ease-out';
      applyTransform();
      // Remove transition after animation
      setTimeout(() => {
        if (imageWrapperRef.current) {
          imageWrapperRef.current.style.transition = 'none';
        }
      }, 200);
    }
  }, [applyTransform]);

  // Handle mouse wheel zoom - direct DOM manipulation for smoothness
  // No state updates during scroll to prevent any re-renders
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isOpen) return;

    let scrollEndTimeout: number | null = null;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      zoomRef.current = Math.max(0.5, Math.min(5, zoomRef.current + delta));
      applyTransform();

      // Update cursor based on zoom level
      if (container) {
        container.style.cursor = zoomRef.current > 1 ? 'grab' : 'default';
      }

      // Update display only after scrolling stops (longer delay)
      if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
      scrollEndTimeout = window.setTimeout(() => {
        setZoomDisplay(zoomRef.current);
      }, 300);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
    };
  }, [isOpen, applyTransform]);

  // Handle mouse down for panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoomRef.current <= 1) return; // Only allow pan when zoomed in
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    panStartRef.current = { x: panRef.current.x, y: panRef.current.y };
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }
  }, []);

  // Handle mouse move for panning - direct DOM manipulation
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    panRef.current = {
      x: panStartRef.current.x + dx,
      y: panStartRef.current.y + dy,
    };
    applyTransform();
  }, [applyTransform]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    if (containerRef.current) {
      containerRef.current.style.cursor = zoomRef.current > 1 ? 'grab' : 'default';
    }
  }, []);

  // Double click to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.transition = 'transform 0.2s ease-out';
    }
    if (zoomRef.current !== 1) {
      resetZoom();
    } else {
      zoomRef.current = 2;
      panRef.current = { x: 0, y: 0 };
      applyTransform();
      setZoomDisplay(2);
    }
    // Remove transition after animation
    setTimeout(() => {
      if (imageWrapperRef.current) {
        imageWrapperRef.current.style.transition = 'none';
      }
    }, 200);
  }, [resetZoom, applyTransform]);

  // Memoize style and callbacks to prevent LocalImage re-renders
  const imageLoadedStyle = useMemo(() => ({ opacity: imageLoaded ? 1 : 0 }), [imageLoaded]);
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);

  const getCategoryById = (id: string): PhotoCategory | undefined => {
    return allCategories.find(c => c.id === id);
  };

  const category = currentPhoto ? getCategoryById(currentPhoto.categoryId) : undefined;

  // Navigation
  const goToPrevious = useCallback(() => {
    setImageLoaded(false);
    resetZoom();
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1));
  }, [photos.length, resetZoom]);

  const goToNext = useCallback(() => {
    setImageLoaded(false);
    resetZoom();
    setCurrentIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0));
  }, [photos.length, resetZoom]);

  // Chronological navigation
  const goToChronoPrev = useCallback(() => {
    if (chronologicalPhotos.length === 0 || chronoIndex < 0) return;
    const prevIdx = chronoIndex > 0 ? chronoIndex - 1 : chronologicalPhotos.length - 1;
    const prevPhoto = chronologicalPhotos[prevIdx];
    const localIdx = photos.findIndex(p => p.id === prevPhoto.id);
    if (localIdx >= 0) {
      setImageLoaded(false);
      resetZoom();
      setCurrentIndex(localIdx);
    } else if (onNavigateToPhoto) {
      onNavigateToPhoto(prevPhoto);
    }
  }, [chronologicalPhotos, chronoIndex, photos, resetZoom, onNavigateToPhoto]);

  const goToChronoNext = useCallback(() => {
    if (chronologicalPhotos.length === 0 || chronoIndex < 0) return;
    const nextIdx = chronoIndex < chronologicalPhotos.length - 1 ? chronoIndex + 1 : 0;
    const nextPhoto = chronologicalPhotos[nextIdx];
    const localIdx = photos.findIndex(p => p.id === nextPhoto.id);
    if (localIdx >= 0) {
      setImageLoaded(false);
      resetZoom();
      setCurrentIndex(localIdx);
    } else if (onNavigateToPhoto) {
      onNavigateToPhoto(nextPhoto);
    }
  }, [chronologicalPhotos, chronoIndex, photos, resetZoom, onNavigateToPhoto]);

  // Preload adjacent images for faster navigation
  useEffect(() => {
    if (!isOpen || !currentPhoto) return;
    const cleanups: (() => void)[] = [];

    // Preload adjacent in current group
    if (photos.length > 1) {
      const prevIdx = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
      const nextIdx = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
      cleanups.push(preloadImage(photos[prevIdx].path));
      cleanups.push(preloadImage(photos[nextIdx].path));
    }

    // Preload chronological neighbors
    if (chronologicalPhotos.length > 1 && chronoIndex >= 0) {
      const prevChronoIdx = chronoIndex > 0 ? chronoIndex - 1 : chronologicalPhotos.length - 1;
      const nextChronoIdx = chronoIndex < chronologicalPhotos.length - 1 ? chronoIndex + 1 : 0;
      cleanups.push(preloadImage(chronologicalPhotos[prevChronoIdx].path));
      cleanups.push(preloadImage(chronologicalPhotos[nextChronoIdx].path));
    }

    return () => cleanups.forEach(fn => fn());
  }, [isOpen, currentPhoto, currentIndex, photos, chronologicalPhotos, chronoIndex]);

  // Zoom by amount with smooth animation
  const zoomBy = useCallback((delta: number) => {
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.transition = 'transform 0.15s ease-out';
    }
    zoomRef.current = Math.max(0.5, Math.min(5, zoomRef.current + delta));
    applyTransform();
    setZoomDisplay(zoomRef.current);
    setTimeout(() => {
      if (imageWrapperRef.current) {
        imageWrapperRef.current.style.transition = 'none';
      }
    }, 150);
  }, [applyTransform]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (chronologicalPhotos.length > 1) {
            goToChronoPrev();
          } else if (photos.length > 1) {
            goToPrevious();
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (chronologicalPhotos.length > 1) {
            goToChronoNext();
          } else if (photos.length > 1) {
            goToNext();
          }
          break;
        case 'i':
          setShowInfo(prev => !prev);
          break;
        case '+':
        case '=':
          zoomBy(0.25);
          break;
        case '-':
        case '_':
          zoomBy(-0.25);
          break;
        case '0':
          resetZoom();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goToPrevious, goToNext, goToChronoPrev, goToChronoNext, photos.length, chronologicalPhotos.length, resetZoom, zoomBy]);

  // Download photo via browser
  const handleDownload = async () => {
    if (!currentPhoto) return;

    try {
      // Use the API serve URL for download
      const url = api.photo.serveUrl(currentPhoto.path);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentPhoto.name + currentPhoto.path.substring(currentPhoto.path.lastIndexOf('.'));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download photo:', error);
    }
  };

  // Open in new browser tab
  const handleOpenExternal = async () => {
    if (!currentPhoto) return;
    try {
      const url = api.photo.serveUrl(currentPhoto.path);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to open photo:', error);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen || !currentPhoto) return null;

  return (
    <div className="photo-preview-overlay" onClick={onClose}>
      <div className="photo-preview" onClick={e => e.stopPropagation()}>
        {/* Top Bar */}
        <div className="photo-preview__header">
          <div className="photo-preview__title">
            <span className="photo-preview__name">{currentPhoto.name}</span>
            {chronologicalPhotos.length > 1 && chronoIndex >= 0 ? (
              <span className="photo-preview__counter" title="Position in chronological order">
                {chronoIndex + 1} / {chronologicalPhotos.length}
              </span>
            ) : photos.length > 1 ? (
              <span className="photo-preview__counter">
                {currentIndex + 1} / {photos.length}
              </span>
            ) : null}
          </div>
          <div className="photo-preview__actions">
            {/* Zoom Controls */}
            <div className="photo-preview__zoom-controls">
              <button
                className="photo-preview__btn"
                onClick={() => zoomBy(-0.25)}
                title="Zoom Out"
                disabled={zoomDisplay <= 0.5}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35M8 11h6" />
                </svg>
              </button>
              <span className="photo-preview__zoom-level">{Math.round(zoomDisplay * 100)}%</span>
              <button
                className="photo-preview__btn"
                onClick={() => zoomBy(0.25)}
                title="Zoom In"
                disabled={zoomDisplay >= 5}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                </svg>
              </button>
              {zoomDisplay !== 1 && (
                <button
                  className="photo-preview__btn"
                  onClick={resetZoom}
                  title="Reset Zoom"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                </button>
              )}
            </div>
            <div className="photo-preview__divider" />
            <button
              className="photo-preview__btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Toggle Info (I)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="9" strokeWidth="3" />
                <circle cx="12" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
                <path d="M9.5 10.5h5v1H13v4h1.5v1h-5v-1H11v-4H9.5z" fill="currentColor" stroke="none" />
              </svg>
            </button>
            <button
              className="photo-preview__btn"
              onClick={handleOpenExternal}
              title="Open in System Viewer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <path d="M15 3h6v6M10 14L21 3" />
              </svg>
            </button>
            <button
              className="photo-preview__btn photo-preview__btn--primary"
              onClick={handleDownload}
              title="Download Photo"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <path d="M7 10l5 5 5-5M12 15V3" />
              </svg>
              <span>Download</span>
            </button>
            {onEditPhoto && (
              <button
                className="photo-preview__btn"
                onClick={() => onEditPhoto(currentPhoto)}
                title="Edit Photo"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            )}
            {onDeletePhoto && (
              <button
                className="photo-preview__btn photo-preview__btn--danger"
                onClick={() => {
                  if (confirm('Delete this photo?')) {
                    onDeletePhoto(currentPhoto.id);
                    if (photos.length === 1) {
                      onClose();
                    } else if (currentIndex >= photos.length - 1) {
                      setCurrentIndex(currentIndex - 1);
                    }
                  }
                }}
                title="Delete Photo"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                </svg>
              </button>
            )}
            <button
              className="photo-preview__btn photo-preview__close"
              onClick={onClose}
              title="Close (Esc)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Image Container */}
        <div
          ref={containerRef}
          className="photo-preview__content"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          style={{ cursor: zoomDisplay > 1 ? 'grab' : 'default' }}
        >
          {/* Navigation Arrows — chronological when available, cluster otherwise */}
          {(chronologicalPhotos.length > 1 || photos.length > 1) && (
            <>
              <button
                className="photo-preview__nav photo-preview__nav--prev"
                onClick={chronologicalPhotos.length > 1 ? goToChronoPrev : goToPrevious}
                title="Previous photo chronologically (Left Arrow)"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                className="photo-preview__nav photo-preview__nav--next"
                onClick={chronologicalPhotos.length > 1 ? goToChronoNext : goToNext}
                title="Next photo chronologically (Right Arrow)"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </>
          )}

          {/* Loading Spinner */}
          {!imageLoaded && (
            <div className="photo-preview__loading">
              <div className="photo-preview__spinner" />
            </div>
          )}

          {/* Main Image with zoom/pan transform - controlled via ref for smooth updates */}
          <div
            ref={imageWrapperRef}
            className="photo-preview__image-wrapper"
          >
            <LocalImage
              src={currentPhoto.path}
              alt={currentPhoto.name}
              className="photo-preview__image"
              style={imageLoadedStyle}
              onLoad={handleImageLoad}
              onError={handleImageLoad}
            />
          </div>

          {/* Info Panel */}
          {showInfo && (
            <div className="photo-preview__info">
              {category && (
                <div className="photo-preview__info-row">
                  <span className="photo-preview__info-label">Category</span>
                  <span className="photo-preview__info-value" style={{ color: category.color }}>
                    <CategoryIconDisplay icon={category.icon} size={14} />
                    <span style={{ marginLeft: '6px' }}>{category.name}</span>
                  </span>
                </div>
              )}

              {currentPhoto.photoType && (
                <div className="photo-preview__info-row">
                  <span className="photo-preview__info-label">Type</span>
                  <span className="photo-preview__info-value">{currentPhoto.photoType}</span>
                </div>
              )}

              {currentPhoto.photoArea && (
                <div className="photo-preview__info-row">
                  <span className="photo-preview__info-label">Area</span>
                  <span className="photo-preview__info-value">{currentPhoto.photoArea}</span>
                </div>
              )}

              {currentPhoto.level && (
                <div className="photo-preview__info-row">
                  <span className="photo-preview__info-label">Level</span>
                  <span className="photo-preview__info-value">{currentPhoto.level}</span>
                </div>
              )}

              <div className="photo-preview__info-row">
                <span className="photo-preview__info-label">Location</span>
                <span className="photo-preview__info-value">
                  {currentPhoto.coordinates.latitude.toFixed(6)}, {currentPhoto.coordinates.longitude.toFixed(6)}
                  {currentPhoto.isManuallyPlaced && (
                    <span className="photo-preview__manual-badge">Manual</span>
                  )}
                </span>
              </div>

              {currentPhoto.timestamp && (
                <div className="photo-preview__info-row">
                  <span className="photo-preview__info-label">Taken</span>
                  <span className="photo-preview__info-value">{formatDate(currentPhoto.timestamp)}</span>
                </div>
              )}

              <div className="photo-preview__info-row">
                <span className="photo-preview__info-label">Added</span>
                <span className="photo-preview__info-value">{formatDate(currentPhoto.addedAt)}</span>
              </div>

              {currentPhoto.notes && (
                <div className="photo-preview__info-row photo-preview__info-row--notes">
                  <span className="photo-preview__info-label">Notes</span>
                  <span className="photo-preview__info-value">{currentPhoto.notes}</span>
                </div>
              )}

              {currentPhoto.description && (
                <div className="photo-preview__info-row photo-preview__info-row--notes">
                  <span className="photo-preview__info-label">Description</span>
                  <span className="photo-preview__info-value">{currentPhoto.description}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Thumbnail Strip for multiple photos */}
        {photos.length > 1 && (
          <div className="photo-preview__thumbnails">
            {photos.map((photo, idx) => (
              <button
                key={photo.id}
                className={`photo-preview__thumb ${idx === currentIndex ? 'photo-preview__thumb--active' : ''}`}
                onClick={() => {
                  setImageLoaded(false);
                  setCurrentIndex(idx);
                }}
              >
                {photo.thumbnailPath ? (
                  <LocalImage src={photo.thumbnailPath} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
